import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../common/functions/cn';
import { Label } from '../label/label';
import { Separator } from '../separator/separator';

const FieldSet = ({ className, ...props }: ComponentProps<'fieldset'>) => {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        'flex flex-col gap-4',
        'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
        className
      )}
      {...props}
    />
  );
};

const FieldLegend = ({
  className,
  variant = 'legend',
  required,
  ...props
}: ComponentProps<'legend'> & { variant?: 'legend' | 'label'; required?: boolean }) => {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      data-required={required || undefined}
      className={cn(
        'mb-2 font-medium leading-snug data-[variant=label]:text-sm data-[variant=legend]:text-base',
        className
      )}
      {...props}
    />
  );
};

const FieldGroup = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-5',
        'data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4',
        className
      )}
      {...props}
    />
  );
};

const fieldVariants = cva('group/field flex w-full gap-2 data-[invalid=true]:text-error', {
  variants: {
    orientation: {
      vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
      horizontal: cn(
        'flex-row items-center *:data-[slot=field-label]:flex-auto',
        'has-[>[data-slot=field-content]]:items-start',
        'has-[>[data-slot=field-content]]:[&>:is([type=checkbox],[type=radio])]:mt-px'
      ),
      responsive: cn(
        'flex-col *:w-full [&>.sr-only]:w-auto',
        '@md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto',
        '@md/field-group:*:data-[slot=field-label]:flex-auto',
        '@md/field-group:has-[>[data-slot=field-content]]:items-start',
        '@md/field-group:has-[>[data-slot=field-content]]:[&>:is([type=checkbox],[type=radio])]:mt-px'
      ),
    },
    variant: {
      default: '',
      card: cn(
        'relative rounded-lg border p-2.5',
        '**:data-[slot=field-label]:after:absolute **:data-[slot=field-label]:after:inset-0',
        'not-has-[:disabled,:checked]:hover:bg-muted/50',
        'has-focus-visible:border-ring has-focus-visible:ring-3 has-focus-visible:ring-ring/50',
        'has-checked:border-ring has-checked:bg-accent has-checked:text-accent-foreground',
        'has-checked:hover:bg-[color-mix(in_oklab,var(--accent),var(--foreground)_4%)]',
        'has-checked:**:data-[slot=field-description]:text-[color-mix(in_oklab,var(--accent-foreground),var(--muted-foreground)_35%)]'
      ),
    },
  },
  defaultVariants: {
    orientation: 'vertical',
    variant: 'default',
  },
});

const Field = ({
  className,
  orientation = 'vertical',
  variant = 'default',
  ...props
}: ComponentProps<'div'> & VariantProps<typeof fieldVariants>) => {
  return (
    <div
      data-slot="field"
      data-orientation={orientation}
      data-variant={variant}
      className={cn(fieldVariants({ orientation, variant }), className)}
      {...props}
    />
  );
};

const FieldContent = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="field-content"
      className={cn('group/field-content flex flex-1 flex-col gap-0.5 leading-snug', className)}
      {...props}
    />
  );
};

const FieldLabel = ({ className, required, ...props }: ComponentProps<typeof Label> & { required?: boolean }) => {
  return (
    <Label
      data-slot="field-label"
      data-required={required || undefined}
      className={cn(
        'group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50',
        className
      )}
      {...props}
    />
  );
};

const FieldTitle = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="field-title"
      className={cn(
        'flex w-fit items-center gap-2 font-medium text-sm group-data-[disabled=true]/field:opacity-50',
        className
      )}
      {...props}
    />
  );
};

const FieldValue = ({ className, isEmpty, children, ...props }: ComponentProps<'div'> & { isEmpty?: boolean }) => {
  const { t } = useTranslation();

  return (
    <div
      data-slot="field-value"
      className={cn('py-1 text-sm', isEmpty && 'text-muted-foreground', className)}
      {...props}
    >
      {isEmpty ? t(($) => $.none) : children}
    </div>
  );
};

const FieldDescription = ({ className, ...props }: ComponentProps<'p'>) => {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'text-left font-normal text-muted-foreground text-sm leading-normal',
        'group-data-[orientation=horizontal]/field:text-balance group-data-[disabled=true]/field:opacity-50',
        'nth-last-2:-mt-1 last:mt-0 [[data-variant=legend]+&]:-mt-2',
        '[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4',
        className
      )}
      {...props}
    />
  );
};

const FieldSeparator = ({ children, className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn('relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2', className)}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          data-slot="field-separator-content"
          className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground"
        >
          {children}
        </span>
      )}
    </div>
  );
};

const FieldError = ({ className, children, ...props }: ComponentProps<'div'>) => {
  if (!children) {
    return null;
  }

  return (
    <div role="alert" data-slot="field-error" className={cn('font-normal text-error text-sm', className)} {...props}>
      {children}
    </div>
  );
};

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
  FieldValue,
};
